import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";

// Context Providers
import { AuthProvider } from "./context/AuthContext";
import { StoreProvider } from "./context/StoreContext";
import { CartProvider } from "./context/CartContext";
import { NotificationProvider } from "./context/NotificationContext";
import { WishlistProvider } from "./context/WishlistContext";

// Scroll to top component
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Components
import ProtectedRoute from "./components/ProtectedRoute";
import StoreProtectedRoute from "./components/StoreProtectedRoute";
import StoreAdminLayout from "./components/StoreAdminLayout";
import CartDrawer from "./components/CartDrawer";

// Dashboard Pages (Admin/Back-office)
import DashboardHome from "./pages/dashboard/DashboardHome";
import ProductsPage from "./pages/dashboard/ProductsPage";
import OrdersPage from "./pages/dashboard/OrdersPage";
import CustomersPage from "./pages/dashboard/CustomersPageV2";
import CustomerDetailPage from "./pages/dashboard/CustomerDetailPage";
import OrderDetailPage from "./pages/dashboard/OrderDetailPage";
import CategoriesPage from "./pages/dashboard/CategoriesPage";
import BlogPage from "./pages/dashboard/BlogPage";
import PaymentsPage from "./pages/dashboard/PaymentsPage";
import ShippingPage from "./pages/dashboard/ShippingPage";
import SettingsPage from "./pages/dashboard/SettingsPage";
import ProductFormPage from "./pages/dashboard/ProductFormPage";
import MarketingPage from "./pages/dashboard/MarketingPage";
import AdminAccountPage from "./pages/dashboard/AdminAccountPage";
import CommerciauxPage from "./pages/dashboard/CommerciauxPage";
import PaymentMethodsPage from "./pages/dashboard/PaymentMethodsPage";
import CmsPagesPage from "./pages/dashboard/CmsPagesPage";
import StockPage from "./pages/dashboard/StockPage";
import MediaPage from "./pages/dashboard/MediaPage";
import IdentityPage from "./pages/dashboard/IdentityPage";
import PopupBannerPage from "./pages/dashboard/PopupBannerPage";
import DeliveryMapPage from "./pages/dashboard/DeliveryMapPage";

// Account Pages (Customer)
import AccountHome from "./pages/account/AccountHome";
import AccountOrders from "./pages/account/AccountOrders";
import AccountProfile from "./pages/account/AccountProfile";

// Storefront Pages
import StorefrontPage from "./pages/storefront/StorefrontPage";
import ProductPage from "./pages/storefront/ProductPage";
import CheckoutPage from "./pages/storefront/CheckoutPage";
import CmsPageView from "./pages/storefront/CmsPageView";
import OrderConfirmationPage from "./pages/storefront/OrderConfirmationPage";
import StoreLoginPage from "./pages/storefront/StoreLoginPage";
import { ForgotPasswordPage, ResetPasswordPage } from "./pages/storefront/PasswordResetPages";
import StoreAccountLayout from "./pages/storefront/StoreAccountLayout";
import MagereSharedLayout from "./pages/storefront/MagereSharedLayout";
import StoreBlogPage from "./pages/storefront/StoreBlogPage";
import StoreBlogPostPage from "./pages/storefront/StoreBlogPostPage";
import WishlistPage from "./pages/storefront/WishlistPage";
import TrackingPage from "./pages/storefront/TrackingPage";

// Admin Login Page
import AdminLoginPage from "./pages/auth/AdminLoginPage";

// Admin extra pages
import SmtpSettingsPage from "./pages/dashboard/SmtpSettingsPage";
import LoyaltySettingsPage from "./pages/dashboard/LoyaltySettingsPage";
import SeoRankPage from "./pages/dashboard/SeoRankPage";
import DriversAdminPage from "./pages/dashboard/DriversAdminPage";
import CompanyPage from "./pages/dashboard/CompanyPage";
import LegalPage from "./pages/dashboard/LegalPage";
import DriverLoginPage from "./pages/driver/DriverLoginPage";
import DriverDashboard from "./pages/driver/DriverDashboard";

// Customer extra pages
import AccountLoyalty from "./pages/account/AccountLoyalty";

// MAGERE Store slug - hardcoded for standalone
const STORE_SLUG = "magere-viandes";

function App() {
  return (
    <AuthProvider>
      <StoreProvider defaultStoreSlug={STORE_SLUG}>
        <CartProvider defaultStoreSlug={STORE_SLUG}>
          <NotificationProvider>
          <WishlistProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              {/* ═══════════ PUBLIC STOREFRONT ROUTES ═══════════ */}
              {/* Homepage - Storefront MAGERE */}
              <Route path="/" element={<StorefrontPage storeSlug={STORE_SLUG} />} />
              
              {/* Product page */}
              <Route path="/product/:productSlug" element={<ProductPage storeSlug={STORE_SLUG} />} />
              
              {/* Checkout */}
              <Route path="/checkout" element={<CheckoutPage storeSlug={STORE_SLUG} />} />
              
              {/* CMS Pages (CGV, Mentions légales, etc.) */}
              <Route path="/page/:pageSlug" element={<CmsPageView storeSlug={STORE_SLUG} />} />
              
              {/* Blog */}
              <Route path="/blog" element={<StoreBlogPage storeSlug={STORE_SLUG} />} />
              <Route path="/blog/:postSlug" element={<StoreBlogPostPage storeSlug={STORE_SLUG} />} />
              
              {/* Order confirmation */}
              <Route path="/confirmation" element={<OrderConfirmationPage storeSlug={STORE_SLUG} />} />
              
              {/* Order tracking (public) */}
              <Route path="/suivi" element={<TrackingPage />} />
              <Route path="/suivi/:trackingCode" element={<TrackingPage />} />
              
              {/* Customer Login */}
              <Route path="/login" element={<StoreLoginPage storeSlug={STORE_SLUG} />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* ═══════════ CUSTOMER ACCOUNT ROUTES ═══════════ */}
              <Route path="/account" element={
                <StoreProtectedRoute storeSlug={STORE_SLUG}>
                  <MagereSharedLayout storeSlug={STORE_SLUG}>
                    <StoreAccountLayout storeSlug={STORE_SLUG}>
                      <AccountHome />
                    </StoreAccountLayout>
                  </MagereSharedLayout>
                </StoreProtectedRoute>
              } />
              <Route path="/account/orders" element={
                <StoreProtectedRoute storeSlug={STORE_SLUG}>
                  <MagereSharedLayout storeSlug={STORE_SLUG}>
                    <StoreAccountLayout storeSlug={STORE_SLUG}>
                      <AccountOrders storeSlug={STORE_SLUG} />
                    </StoreAccountLayout>
                  </MagereSharedLayout>
                </StoreProtectedRoute>
              } />
              <Route path="/account/profile" element={
                <StoreProtectedRoute storeSlug={STORE_SLUG}>
                  <MagereSharedLayout storeSlug={STORE_SLUG}>
                    <StoreAccountLayout storeSlug={STORE_SLUG}>
                      <AccountProfile />
                    </StoreAccountLayout>
                  </MagereSharedLayout>
                </StoreProtectedRoute>
              } />
              <Route path="/account/wishlist" element={
                <StoreProtectedRoute storeSlug={STORE_SLUG}>
                  <MagereSharedLayout storeSlug={STORE_SLUG}>
                    <StoreAccountLayout storeSlug={STORE_SLUG}>
                      <WishlistPage />
                    </StoreAccountLayout>
                  </MagereSharedLayout>
                </StoreProtectedRoute>
              } />
              <Route path="/account/loyalty" element={
                <StoreProtectedRoute storeSlug={STORE_SLUG}>
                  <MagereSharedLayout storeSlug={STORE_SLUG}>
                    <StoreAccountLayout storeSlug={STORE_SLUG}>
                      <AccountLoyalty storeSlug={STORE_SLUG} />
                    </StoreAccountLayout>
                  </MagereSharedLayout>
                </StoreProtectedRoute>
              } />

              {/* ═══════════ ADMIN BACK-OFFICE ROUTES ═══════════ */}
              {/* Admin Login */}
              <Route path="/admin/login" element={<AdminLoginPage />} />
              
              {/* Admin Dashboard - Protected */}
              <Route path="/admin" element={
                <ProtectedRoute allowedRoles={['merchant', 'admin']}>
                  <StoreAdminLayout storeSlug={STORE_SLUG} standalone={true} />
                </ProtectedRoute>
              }>
                <Route index element={<DashboardHome />} />
                <Route path="products" element={<ProductsPage />} />
                <Route path="products/new" element={<ProductFormPage />} />
                <Route path="products/:productId/edit" element={<ProductFormPage />} />
                <Route path="stock" element={<StockPage />} />
                <Route path="media" element={<MediaPage />} />
                <Route path="orders" element={<OrdersPage />} />
                <Route path="orders/:orderId" element={<OrderDetailPage />} />
                <Route path="customers" element={<CustomersPage />} />
              <Route path="customers/:customerId" element={<CustomerDetailPage />} />
              <Route path="categories" element={<CategoriesPage />} />
                <Route path="blog" element={<BlogPage />} />
                <Route path="pages" element={<CmsPagesPage />} />
                <Route path="payments" element={<PaymentsPage />} />
                <Route path="shipping" element={<ShippingPage />} />
                <Route path="customers-map" element={<DeliveryMapPage />} />
                <Route path="marketing" element={<MarketingPage />} />
                <Route path="popups" element={<PopupBannerPage />} />
                <Route path="identity" element={<IdentityPage />} />
                <Route path="stats" element={<DashboardHome />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="notifications" element={<SmtpSettingsPage />} />
                <Route path="loyalty" element={<LoyaltySettingsPage />} />
                <Route path="seo-rank" element={<SeoRankPage />} />
                <Route path="account" element={<AdminAccountPage />} />
                <Route path="commerciaux" element={<CommerciauxPage />} />
                <Route path="payment-methods" element={<PaymentMethodsPage />} />
                <Route path="drivers" element={<DriversAdminPage />} />
                <Route path="company" element={<CompanyPage />} />
                <Route path="legal" element={<LegalPage />} />
              </Route>

              {/* Driver public app */}
              <Route path="/livreur/login" element={<DriverLoginPage />} />
              <Route path="/livreur" element={
                <ProtectedRoute allowedRoles={['driver']}>
                  <DriverDashboard />
                </ProtectedRoute>
              } />

              {/* Legacy routes redirect */}
              <Route path="/store/magere-viandes/*" element={<Navigate to="/" replace />} />
              <Route path="/dashboard/*" element={<Navigate to="/admin" replace />} />
              
              {/* Catch all - redirect to home */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            
            {/* Global Components */}
            <CartDrawer storeSlug={STORE_SLUG} />
            <Toaster position="top-right" richColors />
          </BrowserRouter>
          </WishlistProvider>
          </NotificationProvider>
        </CartProvider>
      </StoreProvider>
    </AuthProvider>
  );
}

export default App;
